/**
 * CobrosPage
 * Bandeja operativa de cobros. Reemplaza Panel + Cartera + Latente en una sola pantalla.
 * KPIs filtrables → tabla densa de cupones · drill-down a créditos críticos abajo.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle, Search, User } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES, PARTNER_ID } from '@/constants';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { useCollectionAttemptsToday } from '@/features/collection/hooks';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import {
  scoreColor,
  creditScore,
  daysUntil,
} from '@/features/credit/utils/creditHealth';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Por Cobrar', path: ROUTES.COLLECTION_DASHBOARD },
];

// ─── Filter types ─────────────────────────────────────────────────────────

type Filter =
  | { kind: 'kpi'; key: 'today' | 'collected' | 'pending' | 'recent' | 'critical' | 'castigados' }
  | { kind: 'tab'; key: 'next7' | 'all' };

interface CuponRow {
  credit: BackofficeCreditsListItem;
  couponNumber: number;
  couponTotal: number;
  /** Saldo pendiente del cupon (descuenta pagos parciales). Es lo que se cobraria. */
  amount: number;
  /** Total nominal del cupon (sin descontar pagos). Para mostrar "X de Y" cuando hay partial. */
  couponTotalMxn: number;
  /** true cuando el cupon vivo tiene paid_amount_mxn > 0 && < total. */
  partialPaid: boolean;
  dueDate: string;
  daysUntilDue: number; // positivo = futuro, negativo = vencido
  /**
   * Si la fila viene de un attempt (cobrado hoy / por intentar hoy / fallido)
   * en vez de derivar del cupón vivo del crédito. Cambia el badge y el monto:
   * SUCCESS/PARTIAL → "Cobrado" / "Cobrado · Parcial" + amount cobrado;
   * PENDING → "Por intentar" + amount esperado;
   * FAILED  → "Fallido" + amount esperado.
   */
  attemptStatus?: 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'PENDING';
  /** Para attempts PARTIAL: lo que efectivamente se cobró (independiente de amount/total). */
  attemptCollectedMxn?: number;
}

type SortKey = 'cliente' | 'credito' | 'cupon' | 'monto' | 'vencimiento' | 'estado' | 'dias';
type SortDir = 'asc' | 'desc';

// ─── Main ─────────────────────────────────────────────────────────────────

export function CobrosPage() {
  const { credits, isLoading: creditsLoading, refetch: refetchCredits } = useBackofficeCredits();
  const { attempts, summary: attemptsSummary, refetch: refetchAttempts } = useCollectionAttemptsToday(PARTNER_ID);
  const isLoading = creditsLoading;
  const refetch = () => {
    refetchCredits();
    refetchAttempts();
  };
  const [filter, setFilter] = useState<Filter>({ kind: 'kpi', key: 'today' });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('vencimiento');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Construir cupones (1 por crédito vivo)
  const cupones: CuponRow[] = useMemo(() => {
    return credits
      .filter((c) => {
        const s = c.status?.toUpperCase();
        return s === 'ACTIVE' || s === 'DELINQUENT';
      })
      .filter((c) => c.next_due_date)
      .map((c) => ({
        credit: c,
        couponNumber: (c.weeks_paid ?? 0) + 1,
        couponTotal: c.weeks_total ?? c.term_weeks ?? 0,
        // Item 030: usamos el saldo pendiente real del cupon vivo (descuenta
        // pagos parciales). Si el backend aun no lo manda (deploy en transito),
        // fallback al monto nominal.
        amount: c.next_due_pending_amount_mxn ?? c.weekly_payment_mxn ?? 0,
        couponTotalMxn: c.next_due_total_mxn ?? c.weekly_payment_mxn ?? 0,
        partialPaid: c.next_due_partial_paid ?? false,
        dueDate: c.next_due_date!,
        daysUntilDue: daysUntil(c.next_due_date!),
      }));
  }, [credits]);

  // Buckets
  const today = useMemo(() => cupones.filter((c) => c.daysUntilDue === 0), [cupones]);
  // Por gestionar = vencidos + hoy + próximos 7 días (todo lo accionable a corto plazo)
  const next7 = useMemo(() => cupones.filter((c) => c.daysUntilDue <= 7), [cupones]);
  const recent = useMemo(() => cupones.filter((c) => c.daysUntilDue < 0 && c.daysUntilDue >= -7), [cupones]);
  const critical = useMemo(() => cupones.filter((c) => c.daysUntilDue < -7), [cupones]);

  // "Cobrados hoy" y "Por intentar hoy" vienen del endpoint
  // /backoffice/collection/attempts/today (item 025-A). Construimos filas
  // directamente desde el attempt (un attempt = una fila) en vez de cruzarlo
  // con `cupones[]`, porque ese array solo tiene el cupon vivo de cada credito
  // y los cupones que ya quedaron PAID hoy no estarian incluidos.
  const creditByCreditId = useMemo(() => {
    const m = new Map<string, BackofficeCreditsListItem>();
    for (const c of credits) m.set(c.credit_id, c);
    return m;
  }, [credits]);

  const attemptToRow = (a: typeof attempts[number]): CuponRow | null => {
    const credit = creditByCreditId.get(a.credit_id);
    if (!credit) return null;
    // Para SUCCESS/PARTIAL el monto es lo cobrado; para PENDING/FAILED es lo esperado.
    const isCollected = a.status === 'SUCCESS' || a.status === 'PARTIAL';
    const displayAmount = isCollected ? a.collected_amount_mxn : a.expected_amount_mxn;
    // dueDate del attempt = cuando se intento (o se intentara). Usamos
    // attempted_at si existe, sino today (PENDING aun no ha corrido).
    const todayStr = new Date().toISOString().slice(0, 10);
    const attemptDate = (a.attempted_at ?? todayStr).slice(0, 10);
    return {
      credit,
      couponNumber: a.coupon_number,
      couponTotal: credit.term_weeks ?? 0,
      amount: displayAmount,
      couponTotalMxn: a.expected_amount_mxn,
      partialPaid: a.status === 'PARTIAL',
      dueDate: attemptDate,
      daysUntilDue: 0,
      attemptStatus: a.status,
      attemptCollectedMxn: a.collected_amount_mxn,
    };
  };

  const collected: CuponRow[] = useMemo(() => {
    return attempts
      .filter((a) => a.status === 'SUCCESS' || a.status === 'PARTIAL')
      .map(attemptToRow)
      .filter((r): r is CuponRow => r !== null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, creditByCreditId]);

  const pending: CuponRow[] = useMemo(() => {
    return attempts
      .filter((a) => a.status === 'PENDING')
      .map(attemptToRow)
      .filter((r): r is CuponRow => r !== null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, creditByCreditId]);

  // Selected bucket
  const selectedBucket = useMemo(() => {
    if (filter.kind === 'kpi') {
      if (filter.key === 'today') return today;
      if (filter.key === 'collected') return collected;
      if (filter.key === 'pending') return pending;
      if (filter.key === 'recent') return recent;
      if (filter.key === 'critical') return critical;
      if (filter.key === 'castigados') return [] as CuponRow[]; // se renderiza con CastigadosSection, no en la bandeja de cupones
    } else {
      if (filter.key === 'next7') return next7;
      if (filter.key === 'all') return cupones;
    }
    return cupones;
  }, [filter, today, collected, pending, recent, critical, next7, cupones]);

  const isCastigadosFilter = filter.kind === 'kpi' && filter.key === 'castigados';

  // Total amounts
  const todayAmount = sumAmount(today);
  // Counts/amounts del endpoint cubren cupones que no necesariamente
  // estan en `cupones[]` (ese array deriva de credit.next_due_date que
  // muestra solo 1 por credito). Usamos el summary del backend.
  const collectedCount = attemptsSummary?.collected_count ?? 0;
  const collectedAmount = attemptsSummary?.collected_amount_mxn ?? 0;
  const pendingCount = attemptsSummary?.pending_count ?? 0;
  const pendingAmount = attemptsSummary?.pending_amount_mxn ?? 0;
  const failedCount = attemptsSummary?.failed_count ?? 0;
  const recentAmount = sumAmount(recent);
  const criticalAmount = sumAmount(critical);

  // Filtered + sorted rows
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = selectedBucket;
    if (q) {
      rows = rows.filter((r) => {
        const name = (r.credit.full_name ?? '').toLowerCase();
        const phone = (r.credit.phone_number ?? '').toLowerCase();
        const id = (r.credit.credit_id ?? '').toLowerCase();
        return name.includes(q) || phone.includes(q) || id.includes(q);
      });
    }
    return [...rows].sort((a, b) => compareCupon(a, b, sortBy, sortDir));
  }, [selectedBucket, search, sortBy, sortDir]);

  // Cualquier crédito con 2+ cupones vencidos (no solo críticos).
  // El backend (item 026) ya expone `overdue_coupons_count`,
  // `overdue_amount_mxn` y `first_overdue_due_date` reales — no hay que
  // aproximar desde `daysUntilDue / 7` (eso fallaba con pagos parciales,
  // adelantos y SETTLEMENT con huecos en el schedule).
  // Excluimos DEFAULTED — esos van a su propio bucket "Castigados",
  // no son mora activa (la cobranza ya cerró ese ciclo).
  const criticosByCredit = useMemo(() => {
    const today = Date.now();
    const dayMs = 1000 * 60 * 60 * 24;
    return credits
      .filter((c) => c.status?.toUpperCase() !== 'DEFAULTED')
      .filter((c) => (c.overdue_coupons_count ?? 0) >= 2)
      .map((c) => {
        const firstOverdue = c.first_overdue_due_date
          ? new Date(c.first_overdue_due_date).getTime()
          : (c.next_due_date ? new Date(c.next_due_date).getTime() : today);
        const daysSinceFirst = Math.max(0, Math.floor((today - firstOverdue) / dayMs));
        return {
          credit: c,
          overdueCoupons: c.overdue_coupons_count ?? 0,
          overdueAmount: c.overdue_amount_mxn ?? 0,
          daysSinceFirst,
        };
      })
      .sort((a, b) => b.daysSinceFirst - a.daysSinceFirst);
  }, [credits]);

  // Castigados (DEFAULTED): bucket separado del flujo de cobranza activa.
  // El monto castigado = overdue_amount_mxn (ya incluye TODOS los cupones
  // WRITTEN_OFF tras el fix del backend, no solo los que tenian due en el
  // pasado).
  const castigadosByCredit = useMemo(() => {
    return credits
      .filter((c) => c.status?.toUpperCase() === 'DEFAULTED')
      .map((c) => ({
        credit: c,
        writtenOffCoupons: c.overdue_coupons_count ?? 0,
        writtenOffAmount: c.overdue_amount_mxn ?? 0,
      }))
      .sort((a, b) => b.writtenOffAmount - a.writtenOffAmount);
  }, [credits]);
  const castigadosTotal = castigadosByCredit.reduce((s, x) => s + x.writtenOffAmount, 0);

  const filterLabel = filterToLabel(filter);
  const filterCount = selectedBucket.length;

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Por Cobrar" subtitle="Bandeja operativa" breadcrumbs={BREADCRUMBS} />
        <div className="space-y-3">
          <Skeleton height={90} variant="rectangular" />
          <Skeleton height={400} variant="rectangular" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Por Cobrar"
        subtitle="Bandeja operativa de cupones por cobrar"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={() => refetch()}>
            <RefreshCw size={16} />
            Actualizar
          </Button>
        }
      />

      <div className="space-y-4">
        {/* KPIs filtrables */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <KpiFilter
            label="Vencen hoy"
            count={today.length}
            amount={todayAmount}
            sub=""
            accent="blue"
            active={filter.kind === 'kpi' && filter.key === 'today'}
            onClick={() => setFilter({ kind: 'kpi', key: 'today' })}
          />
          <KpiFilter
            label="Cobrados hoy"
            count={collectedCount}
            amount={collectedAmount}
            sub={failedCount > 0 ? `${failedCount} fallidos` : ''}
            accent="green"
            active={filter.kind === 'kpi' && filter.key === 'collected'}
            onClick={() => setFilter({ kind: 'kpi', key: 'collected' })}
          />
          <KpiFilter
            label="Por intentar hoy"
            count={pendingCount}
            amount={pendingAmount}
            sub=""
            accent="yellow"
            active={filter.kind === 'kpi' && filter.key === 'pending'}
            onClick={() => setFilter({ kind: 'kpi', key: 'pending' })}
          />
          <KpiFilter
            label="Vencidos recientes"
            count={recent.length}
            amount={recentAmount}
            sub="1-7 días"
            accent="orange"
            active={filter.kind === 'kpi' && filter.key === 'recent'}
            onClick={() => setFilter({ kind: 'kpi', key: 'recent' })}
          />
          <KpiFilter
            label="Vencidos críticos"
            count={critical.length}
            amount={criticalAmount}
            sub="8+ días"
            accent="red"
            active={filter.kind === 'kpi' && filter.key === 'critical'}
            onClick={() => setFilter({ kind: 'kpi', key: 'critical' })}
          />
          <KpiFilter
            label="Castigados"
            count={castigadosByCredit.length}
            amount={castigadosTotal}
            sub="default"
            accent="zinc"
            active={filter.kind === 'kpi' && filter.key === 'castigados'}
            onClick={() => setFilter({ kind: 'kpi', key: 'castigados' })}
          />
        </div>

        {/* Bandeja de cupones — oculta cuando el filtro activo es Castigados,
            porque ese bucket es por crédito (no por cupón) y se renderiza abajo. */}
        {!isCastigadosFilter && (
          <Card>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Bandeja de cupones
                </p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Filtro activo: <span className="font-semibold text-primary">{filterLabel}</span> · {filterCount} cupones
                </p>
              </div>
              <div className="flex items-center gap-2">
                <SecondaryTab
                  label="Por gestionar (≤ 7 días)"
                  count={next7.length}
                  active={filter.kind === 'tab' && filter.key === 'next7'}
                  onClick={() => setFilter({ kind: 'tab', key: 'next7' })}
                />
                <SecondaryTab
                  label="Todos"
                  count={cupones.length}
                  active={filter.kind === 'tab' && filter.key === 'all'}
                  onClick={() => setFilter({ kind: 'tab', key: 'all' })}
                />
              </div>
            </div>

            <div className="mb-3 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search size={14} className="text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nombre, teléfono o ID de crédito..."
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>

            {visibleRows.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No hay cupones que cumplan los filtros.
              </p>
            ) : (
              <CuponTable
                rows={visibleRows.slice(0, 50)}
                total={visibleRows.length}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={(key) => {
                  if (key === sortBy) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                  else { setSortBy(key); setSortDir('asc'); }
                }}
              />
            )}
          </Card>
        )}

        {/* Créditos con cupones múltiples vencidos — solo visible si NO estoy en Castigados */}
        {!isCastigadosFilter && criticosByCredit.length > 0 && (
          <CreditosCriticosSection criticos={criticosByCredit} />
        )}

        {/* Castigados (DEFAULTED) — siempre se muestra como vista principal cuando
            el filtro está activo; si no, va abajo como sección secundaria. */}
        {castigadosByCredit.length > 0 && (
          <CastigadosSection castigados={castigadosByCredit} totalAmount={castigadosTotal} />
        )}
        {isCastigadosFilter && castigadosByCredit.length === 0 && (
          <Card>
            <p className="py-10 text-center text-sm text-muted-foreground">
              No hay créditos castigados.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── KPI Filter ───────────────────────────────────────────────────────────

function KpiFilter({
  label, count, amount, sub, accent, active, onClick,
}: {
  label: string; count: number; amount: number; sub: string;
  accent: 'blue' | 'green' | 'yellow' | 'orange' | 'red' | 'zinc';
  active: boolean; onClick: () => void;
}) {
  const accentBorder = {
    blue: 'border-l-blue-500',
    green: 'border-l-green-500',
    yellow: 'border-l-yellow-500',
    orange: 'border-l-orange-500',
    red: 'border-l-red-500',
    zinc: 'border-l-zinc-500',
  }[accent];
  const valueColor = {
    blue: 'text-foreground',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    orange: 'text-orange-400',
    red: 'text-red-400',
    zinc: 'text-zinc-300',
  }[accent];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-md border bg-card p-3 text-left transition-colors border-l-[3px] ${accentBorder} ${
        active
          ? 'border-primary bg-primary/5 shadow-[0_0_0_1px_var(--color-primary,#3b82f6)]'
          : 'border-border hover:bg-accent/30'
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-extrabold tabular-nums ${valueColor}`}>{formatNumber(count)}</p>
      <p className="text-[11px] text-muted-foreground tabular-nums">
        {formatCurrency(amount, 'MXN', 'es-MX')}
        {sub && <span className="ml-1 opacity-70">· {sub}</span>}
      </p>
      {active && (
        <span
          className="absolute -bottom-2 left-1/2 h-0 w-0 -translate-x-1/2"
          style={{
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid var(--color-primary, #3b82f6)',
          }}
        />
      )}
    </button>
  );
}

function SecondaryTab({
  label, count, active, onClick,
}: {
  label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
      }`}
    >
      {label}
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
        {count}
      </span>
    </button>
  );
}

// ─── Tabla ────────────────────────────────────────────────────────────────

function CuponTable({
  rows, total, sortBy, sortDir, onSort,
}: {
  rows: CuponRow[];
  total: number;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <Th label="Cliente" sortKey="cliente" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Crédito" sortKey="credito" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Cupón" sortKey="cupon" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Monto" sortKey="monto" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
            <Th label="Vencimiento" sortKey="vencimiento" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Estado" sortKey="estado" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Días" sortKey="dias" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
            <th className="w-[36px]" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <CuponTr key={r.credit.credit_id} row={r} />
          ))}
        </tbody>
      </table>

      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
        <span>
          Mostrando {Math.min(rows.length, 50)} de {total} cupones
        </span>
        {total > 50 && <span className="text-muted-foreground/70">Use búsqueda o filtros para acotar</span>}
      </div>
    </div>
  );
}

function Th({
  label, sortKey, sortBy, sortDir, onSort, align,
}: {
  label: string; sortKey: SortKey; sortBy: SortKey; sortDir: SortDir;
  onSort: (k: SortKey) => void; align?: 'left' | 'right';
}) {
  const isSorted = sortBy === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${isSorted ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <span className="text-[9px] opacity-70">
          {isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}

function CuponTr({ row }: { row: CuponRow }) {
  const creditDetailLink = `/credit/detail/${row.credit.credit_id}?directoryId=${row.credit.directory_id}&from=cobros`;
  const expedienteLink = `/backoffice/expedientes/${row.credit.directory_id}?from=cobros`;
  const dueShort = formatDateShort(row.dueDate);
  const status = row.credit.status?.toUpperCase();
  const isOverdue = row.daysUntilDue < 0;
  const dayCell = isOverdue
    ? `vencido ${-row.daysUntilDue} d`
    : row.daysUntilDue === 0
    ? 'hoy'
    : `en ${row.daysUntilDue} d`;
  const dayClass = !isOverdue
    ? row.daysUntilDue === 0 ? 'text-blue-400' : 'text-green-500'
    : -row.daysUntilDue <= 7 ? 'text-orange-400' : 'text-red-400';

  // Si la fila viene de un attempt (Cobrados hoy / Por intentar hoy / Fallidos),
  // el badge refleja el resultado del attempt — no el estado del cupon vivo.
  let badgeClass: string;
  let badgeLabel: string;
  if (row.attemptStatus === 'SUCCESS') {
    badgeClass = 'bg-green-900/40 text-green-400';
    badgeLabel = 'Cobrado';
  } else if (row.attemptStatus === 'PARTIAL') {
    badgeClass = 'bg-green-900/40 text-green-400';
    badgeLabel = 'Cobrado · Parcial';
  } else if (row.attemptStatus === 'PENDING') {
    badgeClass = 'bg-yellow-900/40 text-yellow-400';
    badgeLabel = 'Por intentar';
  } else if (row.attemptStatus === 'FAILED') {
    badgeClass = 'bg-red-900/40 text-red-400';
    badgeLabel = 'Fallido';
  } else {
    // Modo normal (cupon vivo del credito, no attempt)
    badgeClass = !isOverdue
      ? 'bg-blue-900/40 text-blue-400'
      : -row.daysUntilDue <= 7
      ? 'bg-orange-900/40 text-orange-400'
      : 'bg-red-900/40 text-red-400';
    const baseLabel = !isOverdue
      ? status === 'DELINQUENT' ? 'Por intentar' : 'Por vencer'
      : 'Vencido';
    // Item 030: si el cupon vivo tiene pago parcial, lo indicamos en el badge
    // ("Vencido · Parcial" / "Por vencer · Parcial").
    badgeLabel = row.partialPaid ? `${baseLabel} · Parcial` : baseLabel;
  }

  return (
    <tr className="cursor-pointer border-b border-border/30 hover:bg-accent/30">
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <div className="font-medium text-foreground">{row.credit.full_name || '—'}</div>
          <div className="text-[10px] text-muted-foreground">{row.credit.phone_number || '—'}</div>
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <span className="font-mono text-[10px] text-muted-foreground">{row.credit.credit_id.slice(0, 12)}</span>
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block text-foreground">
          {row.couponNumber} / {row.couponTotal}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-right tabular-nums text-foreground">
        <Link to={creditDetailLink} className="block">
          {formatCurrency(row.amount, 'MXN', 'es-MX')}
          {row.partialPaid && (
            <div className="text-[10px] font-normal text-muted-foreground">
              de {formatCurrency(row.couponTotalMxn, 'MXN', 'es-MX')}
            </div>
          )}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-foreground">
        <Link to={creditDetailLink} className="block">
          {dueShort}
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badgeClass}`}>
            {badgeLabel}
          </span>
        </Link>
      </td>
      <td className={`px-2.5 py-2 text-right text-[11px] font-bold tabular-nums ${dayClass}`}>
        <Link to={creditDetailLink} className="block">{dayCell}</Link>
      </td>
      <td className="px-1 py-2 text-right">
        <Link
          to={expedienteLink}
          title="Ver expediente del cliente"
          aria-label="Ver expediente del cliente"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <User size={14} />
        </Link>
      </td>
    </tr>
  );
}

// ─── Créditos críticos ────────────────────────────────────────────────────

interface CriticoRow {
  credit: BackofficeCreditsListItem;
  overdueCoupons: number;
  overdueAmount: number;
  daysSinceFirst: number;
}

function CreditosCriticosSection({ criticos }: { criticos: CriticoRow[] }) {
  const totalCount = criticos.length;
  const totalAmount = criticos.reduce((s, c) => s + c.overdueAmount, 0);
  const top = criticos.slice(0, 10);

  return (
    <div className="rounded-md border border-border border-l-[3px] border-l-red-500 bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-red-400">
          <AlertTriangle size={14} />
          Créditos con cupones múltiples vencidos
        </div>
        <p className="text-[11px] text-muted-foreground">
          <b className="text-foreground">{totalCount}</b> {totalCount === 1 ? 'crédito' : 'créditos'} ·{' '}
          <b className="text-red-400">{formatCurrency(totalAmount, 'MXN', 'es-MX')}</b> en mora acumulada
        </p>
      </div>

      <ul className="flex flex-col">
        {top.map((c) => {
          const score = creditScore(c.credit);
          const color = scoreColor(score);
          const creditDetailLink = `/credit/detail/${c.credit.credit_id}?directoryId=${c.credit.directory_id}&from=cobros`;
          const expedienteLink = `/backoffice/expedientes/${c.credit.directory_id}?from=cobros`;
          return (
            <li key={c.credit.credit_id} className="border-b border-border/30 last:border-b-0">
              <div className="grid grid-cols-[4px_1fr_auto_auto_auto_auto] items-center gap-3 rounded-md px-1 py-2 transition-colors hover:bg-accent/30">
                <div className={`h-8 w-1 rounded-sm ${color.bar}`} />
                <Link to={creditDetailLink} className="flex min-w-0 flex-col gap-0.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">{c.credit.full_name || '—'}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {c.credit.credit_id.slice(0, 12)}
                    </span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {formatCurrency(c.credit.amount_mxn, 'MXN', 'es-MX')} · {c.credit.term_weeks} sem ·{' '}
                    {c.credit.weeks_paid ?? 0}/{c.credit.weeks_total ?? c.credit.term_weeks ?? 0} cuotas pagadas
                  </span>
                </Link>
                <Link to={creditDetailLink} className="rounded bg-red-500/15 px-2.5 py-1 text-center text-[11px] font-bold text-red-400">
                  {c.overdueCoupons} {c.overdueCoupons === 1 ? 'cupón' : 'cupones'} vencidos
                </Link>
                <Link to={creditDetailLink} className="min-w-[80px] text-right text-xs font-bold tabular-nums text-red-400">
                  {formatCurrency(c.overdueAmount, 'MXN', 'es-MX')}
                </Link>
                <Link to={creditDetailLink} className="min-w-[100px] text-right text-[10px] text-muted-foreground">
                  desde hace {c.daysSinceFirst} {c.daysSinceFirst === 1 ? 'día' : 'días'}
                </Link>
                <Link
                  to={expedienteLink}
                  title="Ver expediente del cliente"
                  aria-label="Ver expediente del cliente"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <User size={14} />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>

      {criticos.length > 10 && (
        <p className="mt-2 border-t border-border pt-2 text-center text-[11px] text-muted-foreground">
          Mostrando 10 de {criticos.length} · use el filtro <b>Vencidos críticos</b> arriba para ver todos
        </p>
      )}
    </div>
  );
}

// ─── Castigados (DEFAULTED) ──────────────────────────────────────────────

interface CastigadoRow {
  credit: BackofficeCreditsListItem;
  writtenOffCoupons: number;
  writtenOffAmount: number;
}

function CastigadosSection({ castigados, totalAmount }: { castigados: CastigadoRow[]; totalAmount: number }) {
  const totalCount = castigados.length;
  const top = castigados.slice(0, 10);

  return (
    <div className="rounded-md border border-border border-l-[3px] border-l-zinc-500 bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-300">
          <AlertTriangle size={14} />
          Créditos castigados (default)
        </div>
        <p className="text-[11px] text-muted-foreground">
          <b className="text-foreground">{totalCount}</b> {totalCount === 1 ? 'crédito' : 'créditos'} ·{' '}
          <b className="text-zinc-300">{formatCurrency(totalAmount, 'MXN', 'es-MX')}</b> en castigo
        </p>
      </div>

      <ul className="flex flex-col">
        {top.map((c) => {
          const creditDetailLink = `/credit/detail/${c.credit.credit_id}?directoryId=${c.credit.directory_id}&from=cobros`;
          const expedienteLink = `/backoffice/expedientes/${c.credit.directory_id}?from=cobros`;
          return (
            <li key={c.credit.credit_id} className="border-b border-border/30 last:border-b-0">
              <div className="grid grid-cols-[4px_1fr_auto_auto_auto] items-center gap-3 rounded-md px-1 py-2 transition-colors hover:bg-accent/30">
                <div className="h-8 w-1 rounded-sm bg-zinc-500" />
                <Link to={creditDetailLink} className="flex min-w-0 flex-col gap-0.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">{c.credit.full_name || '—'}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {c.credit.credit_id.slice(0, 12)}
                    </span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {formatCurrency(c.credit.amount_mxn, 'MXN', 'es-MX')} · {c.credit.term_weeks} sem ·{' '}
                    {c.credit.weeks_paid ?? 0}/{c.credit.weeks_total ?? c.credit.term_weeks ?? 0} cuotas pagadas
                  </span>
                </Link>
                <Link to={creditDetailLink} className="rounded bg-zinc-500/15 px-2.5 py-1 text-center text-[11px] font-bold text-zinc-300">
                  {c.writtenOffCoupons} {c.writtenOffCoupons === 1 ? 'cupón' : 'cupones'} castigados
                </Link>
                <Link to={creditDetailLink} className="min-w-[80px] text-right text-xs font-bold tabular-nums text-zinc-300">
                  {formatCurrency(c.writtenOffAmount, 'MXN', 'es-MX')}
                </Link>
                <Link
                  to={expedienteLink}
                  title="Ver expediente del cliente"
                  aria-label="Ver expediente del cliente"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <User size={14} />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>

      {castigados.length > 10 && (
        <p className="mt-2 border-t border-border pt-2 text-center text-[11px] text-muted-foreground">
          Mostrando 10 de {castigados.length}
        </p>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function sumAmount(rows: CuponRow[]): number {
  return rows.reduce((s, r) => s + r.amount, 0);
}

function filterToLabel(f: Filter): string {
  if (f.kind === 'kpi') {
    if (f.key === 'today') return 'Vencen hoy';
    if (f.key === 'collected') return 'Cobrados hoy';
    if (f.key === 'pending') return 'Por intentar hoy';
    if (f.key === 'recent') return 'Vencidos recientes';
    if (f.key === 'critical') return 'Vencidos críticos';
    if (f.key === 'castigados') return 'Castigados (default)';
  } else {
    if (f.key === 'next7') return 'Por gestionar (≤ 7 días)';
    if (f.key === 'all') return 'Todos los cupones vivos';
  }
  return '';
}

function formatDateShort(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function compareCupon(a: CuponRow, b: CuponRow, sortBy: SortKey, dir: SortDir): number {
  const factor = dir === 'asc' ? 1 : -1;
  switch (sortBy) {
    case 'cliente':
      return (a.credit.full_name ?? '').localeCompare(b.credit.full_name ?? '') * factor;
    case 'credito':
      return a.credit.credit_id.localeCompare(b.credit.credit_id) * factor;
    case 'cupon':
      return (a.couponNumber - b.couponNumber) * factor;
    case 'monto':
      return (a.amount - b.amount) * factor;
    case 'vencimiento':
      return (a.dueDate ?? '').localeCompare(b.dueDate ?? '') * factor;
    case 'estado': {
      const aOverdue = a.daysUntilDue < 0 ? 1 : 0;
      const bOverdue = b.daysUntilDue < 0 ? 1 : 0;
      return (aOverdue - bOverdue) * factor;
    }
    case 'dias':
      return (a.daysUntilDue - b.daysUntilDue) * factor;
  }
  return 0;
}
